<template>
  <div
    :class="[$attrs.class, dropdownModifier, 'wrapper-dropdown']"
    :style="$attrs.style">
    <label
      v-if="title"
      :for="selectId"
      class="wrapper-dropdown__label label">
      {{ title }}
    </label>

    <div
      :class="[
        'wrapper-dropdown__field',
        { 'wrapper-dropdown__field--invalid': isInvalid },
        { 'wrapper-dropdown__field--read-only': readOnly },
        { 'wrapper-dropdown__field--disabled': disabled },
        { 'wrapper-dropdown__field--pointer-interaction': pointerInteraction },
      ]"
      @focusout="handleFieldFocusOut"
      @keydown="handleFieldKeyDown"
      @pointerdown="handleFieldPointerDown"
      @click="handleFieldClick">
      <slot />

      <select
        :id="selectId"
        ref="selectElement"
        v-bind="selectAttrs"
        :value="currentValue"
        :disabled="disabled || readOnly"
        :aria-invalid="isInvalid || undefined"
        :class="['wrapper-dropdown__select', selectModifier]"
        @change="handleChange">
        <option
          v-if="defaultValue"
          value=""
          disabled>
          {{ defaultValue }}
        </option>
        <option
          v-for="option in normalizedOptions"
          :key="option.value"
          :value="option.value">
          {{ option.label }}
        </option>
      </select>
      <span class="wrapper-dropdown__icon icon icon--arrow-down" aria-hidden="true"></span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useAttrs, useId } from "vue";
import { openNativeSelect } from "@shared/Components/Dropdown/open-native-select";
import type { DropdownEmits, DropdownProps, NormalizedOption } from "@shared/Components/Dropdown/Dropdown.types";

defineOptions({
  inheritAttrs: false,
});

const props = defineProps<DropdownProps>();

const emit = defineEmits<DropdownEmits>();

const attrs = useAttrs();
const generatedId = useId();
const selectElement = ref<HTMLSelectElement | null>(null);
const pointerInteraction = ref(false);

const normalizedOptions = computed<NormalizedOption[]>(() => {
  const keyValueOptions = props.keyvalueoptions ?? [];
  if (keyValueOptions.length > 0) {
    return keyValueOptions.map((option) => ({
      label: option.key,
      value: option.value,
    }));
  }

  return (props.options ?? []).map((option) => ({
    label: option,
    value: option,
  }));
});

const currentValue = computed(() => props.modelValue ?? props.selectedValue ?? "");
const selectId = computed(() => props.id || generatedId);
const selectAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs;
  return rest;
});

function selectOption(value: string): void {
  if (props.disabled || props.readOnly) {
    return;
  }

  emit("update:modelValue", value);
  emit("onSelect", value);
}

function handleChange(event: Event): void {
  selectOption((event.target as HTMLSelectElement).value);
}

function handleFieldPointerDown(): void {
  pointerInteraction.value = true;
}

function handleFieldClick(event: MouseEvent): void {
  if (event.target instanceof HTMLSelectElement) {
    return;
  }

  openSelect();
}

function handleFieldFocusOut(): void {
  pointerInteraction.value = false;
}

function handleFieldKeyDown(): void {
  pointerInteraction.value = false;
}

function openSelect(): void {
  if (props.disabled || props.readOnly) {
    return;
  }

  const select = selectElement.value;
  if (!select) {
    return;
  }

  openNativeSelect(select);
}
</script>
